import type { ComboboxOption } from '@standardsdigital/ui-components/Combobox';
import { t } from 'i18next';
import * as yup from 'yup';
import { NoYesEnum } from 'redux/services/generate/configurations/serviceLevelDefinitions';

const priceFactorIntegerCount = 10;
export const priceFactorDecimalCount = 4;
export const priceFactorMaxLength =
  priceFactorIntegerCount + priceFactorDecimalCount + 1;

export const CustomCollectionSubscriptionType = 3;

interface EnumClassifier {
  enumValue: number;
  name: string;
}

export interface ServiceLevelFormValues {
  serviceLevelName: string;
  documentServices: number[];
  languages: number[];
  draftStandards: boolean;
  redline: boolean;
  previousVersions: number;
  multiLanguage: boolean;
  subscriptionType: number;
  allowedNsbIds: string[];
  updateExistingSubscriptions: boolean;
  isServiceLevelPriceFactorEnabled: boolean;
  priceFactor?: number;
}

export const defaultServiceLevelFormValues: ServiceLevelFormValues = {
  serviceLevelName: '',
  documentServices: [],
  languages: [],
  draftStandards: false,
  redline: false,
  previousVersions: 0,
  multiLanguage: false,
  subscriptionType: 0,
  allowedNsbIds: [],
  updateExistingSubscriptions: false,
  isServiceLevelPriceFactorEnabled: true,
  priceFactor: undefined,
} as const;

export const getFormSchema = () =>
  yup.object({
    serviceLevelName: yup
      .string()
      .trim(t('serviceLevels.errors.nameRequired'))
      .required(t('serviceLevels.errors.nameRequired'))
      .max(100, t('serviceLevels.errors.nameTooLong')),
    documentServices: yup
      .array()
      .of(yup.number().required())
      .min(1, t('serviceLevels.errors.documentServicesRequired'))
      .required(),
    languages: yup
      .array()
      .of(yup.number().required())
      .min(1, t('serviceLevels.errors.languagesRequired'))
      .required(),
    draftStandards: yup.boolean().required(),
    redline: yup.boolean().required(),
    previousVersions: yup.number().required(),
    multiLanguage: yup.boolean().required(),
    subscriptionType: yup
      .number()
      .required(t('serviceLevels.errors.subscriptionTypeRequired')),
    allowedNsbIds: yup
      .array()
      .of(yup.string().required())
      .min(1, t('serviceLevels.errors.allowedNsbsRequired'))
      .required(),
    isServiceLevelPriceFactorEnabled: yup.boolean().required(),
    priceFactor: yup
      .number()
      .transform((value, originalValue) => {
        if (originalValue === null || originalValue === undefined) {
          return undefined;
        }

        if (typeof originalValue === 'string' && originalValue.trim() === '') {
          return undefined;
        }

        return value as number;
      })
      .optional()
      .test(
        'price-factor-required',
        t('serviceLevels.errors.priceFactorRequired'),
        (value, context) =>
          !(context.parent as ServiceLevelFormValues)
            .isServiceLevelPriceFactorEnabled || value !== undefined,
      )
      .test(
        'price-factor-digits',
        t('serviceLevels.errors.priceFactorInvalid', {
          wholeCount: priceFactorIntegerCount,
          decimalCount: priceFactorDecimalCount,
        }),
        (value) => value === undefined || hasValidPriceFactorDigits(value),
      )
      .test(
        'price-factor-not-zero',
        t('serviceLevels.errors.priceFactorZero'),
        (value) => value === undefined || value !== 0,
      )
      .test(
        'price-factor-not-negative',
        t('serviceLevels.errors.priceFactorNegative'),
        (value) => value === undefined || value >= 0,
      ),
  }) as yup.ObjectSchema<ServiceLevelFormValues>;

export const toNoYesEnum = (value: boolean) =>
  value ? NoYesEnum.Yes : NoYesEnum.No;

export const fromNoYesEnum = (value: NoYesEnum | string | boolean | number) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  return value === NoYesEnum.Yes;
};

const hasValidPriceFactorDigits = (value: number): boolean => {
  if (!Number.isFinite(value)) {
    return false;
  }

  const [wholePart = '', decimalPart = ''] = String(Math.abs(value)).split(/[.,]/);

  return (
    wholePart.length <= priceFactorIntegerCount &&
    decimalPart.length <= priceFactorDecimalCount
  );
};

export const toBitEnumValue = (selectedValues: number[]): number =>
  selectedValues.reduce((acc, value) => acc | value, 0);

export const fromBitEnumValue = (
  bitValue: number,
  classifiers: EnumClassifier[] | undefined,
): number[] =>
  (classifiers ?? [])
    .filter((c) => c.enumValue !== 0 && (bitValue & c.enumValue) === c.enumValue)
    .map((c) => c.enumValue);

export const getBitEnumNames = (
  bitValue: number,
  classifiers: EnumClassifier[] | undefined,
): string[] =>
  (classifiers ?? [])
    .filter((c) => c.enumValue !== 0 && (bitValue & c.enumValue) === c.enumValue)
    .map((c) => c.name);

export const toComboboxOptions = (
  classifiers: EnumClassifier[] | undefined,
  {
    includeNone = false,
  }: { includeNone?: boolean; labelTranslator?: (name: string) => string } = {},
): ComboboxOption[] =>
  (classifiers ?? [])
    .filter((classifier) => includeNone || classifier.enumValue !== 0)
    .map((classifier) => ({
      label: classifier.name,
      value: String(classifier.enumValue),
    }));

export const getSelectedOptions = (
  options: ComboboxOption[],
  selectedValues: number[],
) => options.filter((option) => selectedValues.includes(Number(option.value)));
